import { Div, Button, Input } from '@codesuma/baseline'
import notes, { INote } from '../../../services/notes'
import { RowMenu, RowMenuButton, closeRowMenu, IRowMenuItem } from './row-menu'
import { IDayCell } from '../../../services/calendar'
import style from './index.module.css'

// The day's notes, and the field that writes one. Unlike the day's tasks and
// happenings, a note is written here rather than on a page of its own: it is one
// question with one answer, and the trip to /note and back was longer than the
// thing being written.
//
// An empty day is the field, ready to be typed into — the page puts the caret in
// it when the tab is picked. A day that already holds notes reads as what was
// written: each note whole, with the two things that can be done to it, and the
// line that opens the field again for another.
//
// Changing an existing note still goes to /note, which is the one place a note
// is edited. The note itself is the way there, as a task's title is.

// What a note's dots offer. Fewer than a task's: a note is not done or put
// off, it is only rewritten or thrown away.
const OPTIONS: IRowMenuItem[] = [
    { action: 'edit', label: 'ویرایش' },
    { action: 'remove', label: 'حذف', danger: true },
]

// Material Design "add" icon (Apache 2.0), inherits currentColor.
const PLUS_SVG = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z"/></svg>'

export const Notes = (day: IDayCell) => {
    const base = Div()
    base.addClass(style.notes)

    // The open field, when there is one. The page reaches for it through
    // focusField() after it switches to this tab.
    let field: { focus: () => void } | null = null

    // A note as it was written, with its own dots beside it — the same tap in
    // the same column a task's row keeps them in.
    const row = (note: INote) => {
        const wrap = Div()
        wrap.addClass(style.noteRow)

        const open = () => base.emit('open-note', note.id)

        // Tapping the note is the dots' ویرایش without the menu.
        const text = Button(note.text)
        text.addClass(style.noteText)
        text.on('click', open)
        wrap.append(text)

        const more = RowMenuButton('گزینه‌های یادداشت')
        more.on('click', () => {
            const menu = RowMenu(more.el, OPTIONS)
            menu.on('pick', (action: string) => {
                if (action === 'edit') return open()
                notes.remove(note.id)
                render(false)
            })
        })
        wrap.append(more)

        return wrap
    }

    // The field, and nothing beside it. It writes itself: there is no button to
    // press and no moment where what has been typed is not kept, which is what
    // the placeholder promises. The tab does not redraw when it writes — that
    // would take the caret out of the reader's sentence. The note appears as a
    // row the next time the tab is opened, which is when there is a reason to
    // read it rather than to go on writing it.
    const composer = () => {
        const wrap = Div()
        wrap.addClass(style.composer)

        const input = Input('یادداشت، نکته یا خاطره‌ای بنویس؛ (به صورت خودکار ذخیره می‌شود)', 'textarea')
        input.addClass(style.composerField)
        input.attr('dir', 'auto')
        wrap.append(input)

        // The note this field has already written, once it has written one, so
        // the next keystroke changes that note rather than starting another.
        let id = ''
        let written = ''
        let pending = 0

        const write = () => {
            pending = 0
            const text = input.value()
            if (text.trim() === written.trim()) return
            written = text
            // An emptied note is dropped by the store rather than kept as a
            // blank row, so the id it had is gone with it — anything typed
            // after that starts a new one.
            if (id) notes.update(id, text)
            else id = notes.add(day, text)?.id ?? ''
            if (!text.trim()) id = ''
        }

        // A beat after the typing stops, so a sentence is one write and not
        // twenty.
        input.on('input', () => {
            clearTimeout(pending)
            pending = window.setTimeout(write, 700)
        })
        // And the moment the field is left or taken away — the tab switched,
        // the day swiped, the page closed — whatever is in it is kept.
        input.on('blur', write)
        wrap.on('unmounted', () => {
            clearTimeout(pending)
            write()
        })

        field = input
        return wrap
    }

    // One more line of the list rather than a button on the end of it — the same
    // line the tasks and the happenings carry.
    const addLine = () => {
        const add = Button()
        add.addClass(style.addRow, style.columnAdd)
        add.html(`${PLUS_SVG}<span>افزودن یادداشت دیگر</span>`)
        add.on('click', () => {
            render(true)
            field?.focus()
        })
        return add
    }

    // `writing` is the add line having been pressed. An empty day is always
    // writing: there is nothing else for the tab to be.
    function render(writing: boolean) {
        base.empty()
        field = null
        const rows = notes.forDay(day)
        for (const note of rows) base.append(row(note))
        base.append(writing || rows.length === 0 ? composer() : addLine())
    }

    render(false)

    // The menu is hung off the body, so it has to be put away with the tab
    // rather than left behind when the slide redraws.
    base.on('unmounted', closeRowMenu)

    return Object.assign(base, {
        // The caret, for the page that has just switched to this tab. Nothing
        // happens on a day whose notes are being read rather than written.
        focusField: () => field?.focus(),
    })
}
